"use client";

import { Modal } from "@cmp-src/overlay/modals/Modal";
import { Button } from "@cmp-src/primitives/button";
import { Input } from "@cmp-src/primitives/input";
import {
  setVercelAuthToken,
  setVercelSlug
} from "@gettactic/helpers/src/vercel/vercelPreview";
import React from "react";

// Re-exported so existing consumers keep importing from this module.
export {
  clearVercelAuthToken,
  clearVercelSlug,
  getVercelAuthToken,
  getVercelSlug,
  isVercelDeployment,
  setVercelAuthToken,
  setVercelSlug
} from "@gettactic/helpers/src/vercel/vercelPreview";

export interface SlugSelectorProps {
  onSubmit: (slug: string, authToken: string) => void;
  /** Pre-fill the slug field (when changing an already-saved value). */
  initialSlug?: string;
  /** Pre-fill the auth token field so changing the slug doesn't require re-typing it. */
  initialAuthToken?: string;
  /**
   * When provided, the modal becomes dismissable (close button + Cancel) and
   * calling it leaves the saved values untouched. Omit it for the initial
   * setup, where values are mandatory to proceed.
   */
  onCancel?: () => void;
}

/**
 * Modal component that asks for organization slug and auth token on Vercel deployments.
 * This allows testing preview deployments with any organization.
 */
export const SlugSelector: React.FC<SlugSelectorProps> = ({
  onSubmit,
  initialSlug = "",
  initialAuthToken = "",
  onCancel
}) => {
  const [slug, setSlug] = React.useState(initialSlug);
  const [authToken, setAuthToken] = React.useState(initialAuthToken);
  const [error, setError] = React.useState("");
  const isDismissable = !!onCancel;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedSlug = slug.trim().toLowerCase();
    const trimmedToken = authToken.trim();

    if (!trimmedSlug) {
      setError("Please enter an organization slug");
      return;
    }

    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(trimmedSlug)) {
      setError(
        "Invalid slug format (use lowercase letters, numbers, and hyphens)"
      );
      return;
    }

    if (!trimmedToken) {
      setError("Please enter an authentication token");
      return;
    }

    setVercelSlug(trimmedSlug);
    setVercelAuthToken(trimmedToken);
    onSubmit(trimmedSlug, trimmedToken);
  };

  // During the initial setup the user must enter values to proceed, so the
  // modal can't be dismissed. When changing an existing value, allow closing
  // it without touching what's already saved.
  const noop = () => {};

  return (
    <Modal
      isOpen={true}
      onClose={onCancel ?? noop}
      title="Vercel Preview Mode"
      size="sm"
      closeOnOverlayClick={isDismissable}
      hideCloseButton={!isDismissable}
    >
      <p className="mb-4 text-sm text-muted-foreground">
        Enter the organization slug and authentication token to test this
        preview deployment.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="slug" className="mb-1 block text-sm font-medium">
            Organization Slug
          </label>
          <Input
            type="text"
            value={slug}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSlug(e.target.value);
              setError("");
            }}
            placeholder="e.g., my-company"
            className="w-full"
            autoFocus
          />
        </div>

        <div className="mb-4">
          <label htmlFor="authToken" className="mb-1 block text-sm font-medium">
            Auth Token
          </label>
          <Input
            type="password"
            value={authToken}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setAuthToken(e.target.value);
              setError("");
            }}
            placeholder="Bearer token or session token"
            className="w-full"
          />
        </div>

        {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

        <Modal.Footer>
          {onCancel ? (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
          <Button
            style={{
              color: "white",
              borderRadius: "10px",
              background: "#142222"
            }}
            type="submit"
          >
            Continue
          </Button>
        </Modal.Footer>
      </form>

      <p className="mt-4 text-xs text-muted-foreground">
        These values are saved in localStorage and will be used for all API
        requests on this Vercel deployment.
      </p>
    </Modal>
  );
};
